import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  isManuallyAddedCapacityRow,
  isValidCapacityEmail,
} from '../../utils/capacityPlanningUtils';

const CustomEditableCellRenderer = (props) => {
  const [value, setValue] = useState(props.value);
  const [isEditing, setIsEditing] = useState(false);
  const theme = useSelector((state) => state.theme.theme);
  const colId = props.column.getColId();
  const isDisabled = !!props.colDef.cellRendererParams?.disabled;
  const isNumberColumn = props.colDef.cellDataType === 'number';
  const isRole = colId === 'role';
  const isEmail = props.colDef.field === 'email';
  const isAvailableHours = props.colDef.field === 'availableHours';
  const isAllocationType = colId === 'allocationType';
  const isLeaves = props.colDef.field === 'leaves';
  const isIssueType = colId === 'issueType';
  const isTeamMemberName = props.colDef.field === 'name';
  const emailMarkedInvalid =
    isEmail && props.colDef.cellRendererParams?.emailHasError?.(props.data?.id);
  const emailValue = value === null || value === undefined ? '' : String(value);
  const emailHasError =
    emailMarkedInvalid &&
    !!emailValue.trim() &&
    !isValidCapacityEmail(emailValue);
  const emailInputRef = useRef(null);
  const canEditTeamMemberName =
    isTeamMemberName &&
    isManuallyAddedCapacityRow(props.data) &&
    !props.colDef.cellRendererParams?.disabled;

 const isEditable =
  !isDisabled &&
  (isRole ||
    isEmail ||
    isAvailableHours ||
    isLeaves ||
    isAllocationType ||
    canEditTeamMemberName);

  useEffect(() => {
    setValue(props.value);
  }, [props.value]);

  useEffect(() => {
    if (!isEmail || !isEditing) return;
    const frame = requestAnimationFrame(() => {
      emailInputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [isEmail, isEditing]);

  const triggerUpdate = (newValue) => {
    const updatedRow = {
      ...props.data,
      [props.colDef.field]: newValue,
    };

    if (props.colDef.cellRendererParams?.handleInputChange) {
      props.colDef.cellRendererParams.handleInputChange(
        props.data.id,
        props.colDef.field,
        newValue,
      );
    }

    if (props.context?.onCellValueChanged) {
      props.context.onCellValueChanged({
        data: updatedRow,
        colDef: props.colDef,
        node: props.node,
        newValue,
        oldValue: props.value,
        field: props.colDef.field,
        api: props.api,
        column: props.column,
        rowIndex: props.rowIndex,
      });
    }
  };

  const handleChange = (e) => {
    const newValue = isNumberColumn ? Number(e.target.value) : e.target.value;
    setValue(newValue);
    triggerUpdate(newValue);
  };

  const handleBlur = () => {
    setIsEditing(false);
    triggerUpdate(value);
  };

  const handleClick = () => {
    if (isEditable) {
      setIsEditing(true);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsEditing(false);
      if (e.key === 'Enter') {
        triggerUpdate(value);
      }
    }
  };

  const cellStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '8px',
    fontSize: '13px',
    color: theme === 'light' ? '#202020' : 'white',
    backgroundColor: 'transparent',
    cursor: isEditable ? 'pointer' : 'default',
    border: 'none',
    outline: 'none',
    textAlign: isNumberColumn ? 'center' : 'left',
  };

  const inputStyle = {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: theme === 'light' ? '#202020' : 'white',
    fontSize: '13px',
    textAlign: isNumberColumn ? 'center' : 'left',
    padding: '0',
    WebkitAppearance: 'none',
    MozAppearance: 'textfield',
  };

  const selectStyle = {
    width: '100%',
    height: '100%',
    border: 'none',
    outline: 'none',
    backgroundColor: theme === 'light' ? '#FFFFFF' : '#2c3e50',
    color: theme === 'light' ? '#202020' : 'white',
    fontSize: '13px',
    padding: '0 20px 0 0',
    cursor: 'pointer',
    backgroundImage: theme === 'light' 
      ? `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23202020' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`
      : `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 4px center',
    backgroundSize: '12px',
    WebkitAppearance: 'none',
    MozAppearance: 'none',
  };

  if (isEmail) {
    const inputWrapClass = `capacity-planning-email-input-wrap${
      emailHasError ? ' capacity-planning-email-input-wrap--error' : ''
    }`;

    const emailInputStyle = {
      width: '100%',
      padding: '2px 8px',
      border: 'none',
      outline: 'none',
      fontSize: '13px',
      lineHeight: '1.25',
      color: theme === 'light' ? '#202020' : '#e5e7eb',
      backgroundColor: 'transparent',
      boxSizing: 'border-box',
    };

    const renderEmailStack = (inputControl) => (
      <div className="capacity-planning-email-cell-inner">
        <div className="capacity-planning-email-stack">
          <div className={inputWrapClass}>{inputControl}</div>
          {emailHasError && (
            <span className="capacity-planning-email-error" role="alert">
              Invalid email address
            </span>
          )}
        </div>
      </div>
    );

    if (isEditing || emailHasError) {
      return renderEmailStack(
        <input
          ref={emailInputRef}
          type="text"
          inputMode="email"
          autoComplete="off"
          value={emailValue}
          onChange={(e) => {
            setValue(e.target.value);
            triggerUpdate(e.target.value);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={emailInputStyle}
          placeholder="Enter email"
          className="capacity-planning-email-input"
        />,
      );
    }

    return renderEmailStack(
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
        className="capacity-planning-email-input capacity-planning-email-display"
        style={{ ...emailInputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
      >
        {value === null || value === undefined || value === '' ? (
          <span style={{ opacity: 0.6 }}>Enter email</span>
        ) : (
          value
        )}
      </div>,
    );
  }

  if (canEditTeamMemberName) {
    if (isEditing) {
      return (
        <input
          type="text"
          value={value === null || value === undefined ? '' : value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          style={inputStyle}
          placeholder="Team member"
        />
      );
    }
    return (
      <div onClick={handleClick} style={{ ...cellStyle, cursor: 'pointer' }}>
        {value === null || value === undefined || value === '' ? (
          <span style={{ opacity: 0.6 }}>Enter name</span>
        ) : (
          value
        )}
      </div>
    );
  }

  if (isRole) {
    const roleOptions = props.colDef.cellRendererParams?.roles || [];
    // Always render the <select> so a single click opens the options
    // (swapping a div for a select on click needed a second click).
    return (
      <select
        value={value || ''}
        onChange={handleChange}
        disabled={!isEditable}
        style={{ ...selectStyle, padding: '0 24px 0 8px', backgroundPosition: 'right 8px center' }}
      >
        <option value="" style={{ backgroundColor: theme === 'light' ? '#FFFFFF' : '#2c3e50', color: theme === 'light' ? '#202020' : 'white' }}>
          Select Role
        </option>
        {roleOptions.map((role, idx) => (
          <option
            key={idx}
            value={role.role}
            style={{ backgroundColor: theme === 'light' ? '#FFFFFF' : '#2c3e50', color: theme === 'light' ? '#202020' : 'white' }}
          >
            {role.role}
          </option>
        ))}
      </select>
    );
  }
if (isIssueType) {
  const issueTypeOptions = props.colDef.cellRendererParams?.issueTypes || [];
  const handleIssueTypeChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (props.context?.onCellValueChanged) {
      props.context.onCellValueChanged({
        data: props.data,
        newValue,
        field: props.colDef.field,
        colDef: props.colDef,
        node: props.node,
        api: props.api,
        column: props.column,
        rowIndex: props.rowIndex,
      });
    }
  };

  const selectStyle = {
    height: '28px',
    backgroundImage: theme === 'light' 
      ? `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e%3cpath stroke='%23202020' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 1 4 4 4-4'/%3e%3c/svg%3e")`
      : `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 10 6'%3e%3cpath stroke='%23D9E4F1' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m1 1 4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 8px center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '10px 6px',
    paddingRight: '24px'
  };

  return (
    <div className="relative inline-block w-full">
      <select
        value={value || ''}
        onChange={handleIssueTypeChange}
        className={`w-full px-2 py-1 text-sm rounded cursor-pointer focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none ${
          theme === 'light' 
            ? 'text-[#202020] bg-[#FFFFFF] border border-[#E5E5E5] hover:bg-[#F7F9FF]' 
            : 'text-[#D9E4F1] bg-[#182433] border border-[#1F2F41] hover:bg-[#1E2B3A]'
        }`}
        style={selectStyle}
      >
        {issueTypeOptions.map((type, idx) => (
          <option 
            key={idx} 
            value={type}
            className={theme === 'light' ? 'bg-[#FFFFFF] text-[#202020]' : 'bg-[#182433] text-[#D9E4F1]'}
          >
            {type}
          </option>
        ))}
      </select>
    </div>
  );
}

  if (isAvailableHours || isLeaves) {
    if (isEditing) {
      return (
        <input
          type="number"
          min={0}
          value={value === null || value === undefined ? '' : value}
          onChange={(e) => setValue(Number(e.target.value))}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          style={inputStyle}
        />
      );
    }

    return (
      <div onClick={handleClick} style={cellStyle}>
        {value === null || value === undefined ? '0' : value}
      </div>
    );
  }

  if (isEmail) {
    if (isEditing) {
      return (
        <input
          type="email"
          value={value ?? ''}
          placeholder="Enter email"
          onChange={(e) => setValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          style={inputStyle}
        />
      );
    }
    return (
      <div onClick={handleClick} style={cellStyle}>
        {value ? value : <span style={{ opacity: 0.5 }}>Enter email</span>}
      </div>
    );
  }

  if (isAllocationType) {
    const options = [
      { value: 'Partial', label: 'Partial' },
      { value: 'Full', label: 'Full' },
    ];
    // The column's valueGetter returns the 'Select Option' placeholder for empty values
    const selectedValue = value === 'Select Option' || value == null ? '' : value;
    return (
      <select
        value={selectedValue}
        onChange={handleChange}
        disabled={!isEditable}
        style={{ ...selectStyle, padding: '0 24px 0 8px', backgroundPosition: 'right 8px center' }}
      >
        <option value="" style={{ backgroundColor: theme === 'light' ? '#FFFFFF' : '#2c3e50', color: theme === 'light' ? '#202020' : 'white' }}>
          Select Option
        </option>
        {options.map((opt, idx) => (
          <option
            key={idx}
            value={opt.value}
            style={{ backgroundColor: theme === 'light' ? '#FFFFFF' : '#2c3e50', color: theme === 'light' ? '#202020' : 'white' }}
          >
            {opt?.label}
          </option>
        ))}
      </select>
    );
  }

  return <div style={cellStyle}>{value === null || value === undefined ? '' : value}</div>;
};

CustomEditableCellRenderer.propTypes = {
  value: PropTypes.any,
  node: PropTypes.object.isRequired,
  colDef: PropTypes.shape({
    field: PropTypes.string,
    cellDataType: PropTypes.string,
    cellRendererParams: PropTypes.shape({
      disabled: PropTypes.bool,
      handleInputChange: PropTypes.func,
      roles: PropTypes.array,
      issueTypes: PropTypes.array,
      emailHasError: PropTypes.func,
    }),
  }).isRequired,
  column: PropTypes.object.isRequired,
  data: PropTypes.object,
  context: PropTypes.object,
  rowIndex: PropTypes.number,
  api: PropTypes.object,
};

export default CustomEditableCellRenderer;
